import HttpService from './HttpService';

class AuthService extends HttpService {
  /**
   * Login do usuário com suporte a reCAPTCHA v3
   * @param {string} email - Email do usuário
   * @param {string} password - Senha do usuário
   * @param {string} mfaCodeOrRecaptcha - Código MFA (4 dígitos) OU token reCAPTCHA (primeiro login)
   * @param {string} recaptchaToken - Token reCAPTCHA para validação MFA (opcional)
   * @returns {Promise} Resposta da API
   */
  login(email, password, mfaCodeOrRecaptcha = null, recaptchaToken = null) {
    const payload = {
      email,
      password,
    };

    // Detectar se é código MFA (4 dígitos) ou token reCAPTCHA
    if (mfaCodeOrRecaptcha) {
      const isMfaCode = /^\d{4}$/.test(mfaCodeOrRecaptcha);

      if (isMfaCode) {
        // É código MFA
        payload.mfaCode = mfaCodeOrRecaptcha;
        // Token reCAPTCHA vem no 4º parâmetro para validação MFA
        if (recaptchaToken) {
          payload.recaptchaToken = recaptchaToken;
        }
      } else {
        // É token reCAPTCHA do primeiro login
        payload.recaptchaToken = mfaCodeOrRecaptcha;
      }
    }

    return this.post("/auth/login", payload).then((response) => {
      if (response.data.access_token) {
        localStorage.setItem("token", response.data.access_token);
        localStorage.setItem("user", JSON.stringify(response.data.user));

        // ✅ refresh_token agora vem via cookie httpOnly
        // Não precisa armazenar manualmente no localStorage
      }
      return response.data;
    });
  }

  /**
   * Renovar access token usando refresh token
   * Implementa token rotation: gera novo par de tokens e revoga o antigo
   * ✅ refresh_token é enviado automaticamente via cookie httpOnly
   * @returns {Promise} Novo access_token
   */
  async refresh() {
    try {
      // ✅ Cookie com refresh_token é enviado automaticamente pelo navegador
      // Não precisa enviar nada no body
      const response = await this.post("/auth/refresh", {});

      // Armazenar novo access_token (token rotation)
      if (response.data.access_token) {
        localStorage.setItem("token", response.data.access_token);
      }

      // ✅ Novo refresh_token vem atualizado via Set-Cookie header
      // Navegador atualiza o cookie automaticamente

      return response.data;
    } catch (error) {
      // Se refresh falhar, limpar tokens e forçar novo login
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      throw error;
    }
  }

  getUser() {
    const user = localStorage.getItem("user");
    return user ? JSON.parse(user) : null;
  }

  /**
   * Logout do usuário - revoga refresh token no backend
   * ✅ refresh_token é enviado automaticamente via cookie httpOnly
   * @returns {Promise} Resposta da API
   */
  async logout() {
    try {
      // ✅ Cookie com refresh_token é enviado automaticamente
      // Backend lê do cookie e revoga o token
      await this.post("/auth/logout", {});
    } catch (error) {
      console.error("Erro ao revogar refresh token:", error);
      // Continuar com logout local mesmo se a chamada falhar
    } finally {
      // Limpar localStorage (cookie é limpo pelo backend via clearCookie)
      localStorage.removeItem("token");
      localStorage.removeItem("user");
    }
  }

  /**
   * Logout em todos os dispositivos - revoga todos os refresh tokens do usuário
   * @returns {Promise} Resposta da API
   */
  async logoutAll() {
    try {
      await this.post("/auth/logout-all");
    } finally {
      // Limpar localStorage local (cookie é limpo pelo backend)
      localStorage.removeItem("token");
      localStorage.removeItem("user");
    }
  }

  /**
   * Registro de novo usuário com suporte a reCAPTCHA v3
   * @param {Object} userData - Dados do usuário (email, password, etc)
   * @param {string} recaptchaToken - Token reCAPTCHA v3
   * @returns {Promise} Resposta da API
   */
  register(userData, recaptchaToken = null) {
    const payload = { ...userData };

    if (recaptchaToken) {
      payload.recaptchaToken = recaptchaToken;
    }

    return this.post("/auth/register", payload);
  }

  isAuthenticated() {
    return !!localStorage.getItem("token");
  }

  /**
   * Solicitar recuperação de senha com suporte a reCAPTCHA v3
   * @param {string} email - Email do usuário
   * @param {string} recaptchaToken - Token reCAPTCHA v3
   * @returns {Promise} Resposta da API
   */
  forgotPassword(email, recaptchaToken = null) {
    const payload = { email };

    if (recaptchaToken) {
      payload.recaptchaToken = recaptchaToken;
    }

    return this.post("/auth/forgot-password", payload);
  }

  /**
   * Redefinir senha com suporte a reCAPTCHA v3
   * @param {string} token - Token de reset recebido por email
   * @param {string} password - Nova senha
   * @param {string} recaptchaToken - Token reCAPTCHA v3
   * @returns {Promise} Resposta da API
   */
  resetPassword(token, password, recaptchaToken = null) {
    const payload = { token, password };

    if (recaptchaToken) {
      payload.recaptchaToken = recaptchaToken;
    }

    return this.post("/auth/reset-password", payload).then(
      (response) => response.data
    );
  }

  // Métodos MFA
  enableMfa() {
    return this.post("/auth/mfa/enable").then((response) => response.data);
  }

  disableMfa() {
    return this.post("/auth/mfa/disable").then((response) => response.data);
  }

  getMfaStatus() {
    return this.get("/auth/mfa/status").then((response) => response.data);
  }

  sendMfaCode(userId) {
    return this.post("/auth/mfa/send-code", { userId }).then(
      (response) => response.data
    );
  }

  verifyMfaCode(userId, code) {
    return this.post("/auth/mfa/verify-code", { userId, code }).then(
      (response) => response.data
    );
  }
}

export default AuthService;